import { create } from "zustand";
import type { ChatMessage, SuggestedChip } from "@/lib/api/chat";
import { filterVisibleSuggestedChips } from "@/lib/chat/visible-suggestions";

/** Safety cap — oldest messages are pruned when this limit is exceeded. */
export const MAX_MESSAGES = 100;

// ---------------------------------------------------------------------------
// Chat preferences — consolidated session-only persistence via sessionStorage
// ---------------------------------------------------------------------------

export const CHAT_PREFS_KEY = "spectree-chat-preferences";

interface PanelSize {
  width: number;
  height: number;
}

export type DockMode = "bottom" | "right";

interface ChatPreferences {
  panelSize: PanelSize;
  isSoundEnabled: boolean;
  isQuietMode: boolean;
  dockMode: DockMode;
}

const DEFAULT_PANEL_SIZE: PanelSize = { width: 400, height: 500 };

const DEFAULT_PREFS: ChatPreferences = {
  panelSize: DEFAULT_PANEL_SIZE,
  isSoundEnabled: true,
  isQuietMode: false,
  dockMode: "right",
};

function loadChatPreferences(): ChatPreferences {
  try {
    const raw = sessionStorage.getItem(CHAT_PREFS_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null) {
        const p = parsed as Record<string, unknown>;
        const ps = p.panelSize;
        return {
          panelSize:
            typeof ps === "object" &&
            ps !== null &&
            "width" in ps &&
            "height" in ps &&
            typeof (ps as PanelSize).width === "number" &&
            typeof (ps as PanelSize).height === "number"
              ? (ps as PanelSize)
              : DEFAULT_PANEL_SIZE,
          isSoundEnabled:
            typeof p.isSoundEnabled === "boolean"
              ? p.isSoundEnabled
              : DEFAULT_PREFS.isSoundEnabled,
          isQuietMode:
            typeof p.isQuietMode === "boolean"
              ? p.isQuietMode
              : DEFAULT_PREFS.isQuietMode,
          dockMode:
            typeof p.dockMode === "string" &&
            (p.dockMode === "bottom" || p.dockMode === "right")
              ? (p.dockMode as DockMode)
              : DEFAULT_PREFS.dockMode,
        };
      }
    }
  } catch {
    // Ignore corrupt sessionStorage data
  }
  return DEFAULT_PREFS;
}

function saveChatPreferences(prefs: ChatPreferences): void {
  try {
    sessionStorage.setItem(CHAT_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // sessionStorage may be unavailable (e.g. private browsing quota)
  }
}

// ---------------------------------------------------------------------------
// Onboarding progress — persisted to localStorage
// ---------------------------------------------------------------------------

export const ONBOARDING_KEY = "spectree-onboarding-progress";

export type Platform = "mac" | "windows-powershell" | "windows-cmd";
export type DesignerPlatform = "web" | "ios" | "android";

export interface OnboardingProgress {
  // Base fields (from ENG-E244 design)
  hasCreatedEpic: boolean;
  hasCreatedRequest: boolean;
  hasRunOrchestration: boolean;
  setupCompleted: boolean;
  lastOnboardingStep: number;
  dismissed: boolean;
  // Help Center & Wizard fields (ENG-E245)
  hasCompletedWizard: boolean;
  currentWizardStep: number;
  firstLoginAt: string | null;
  completedAt: string | null;
  selectedPlatform: Platform | null;
  selectedDesignerPlatform: DesignerPlatform | null;
  completedFlows: string[];
  completedSteps: string[];
  hasSeenHelpTooltip: boolean;
}

const DEFAULT_ONBOARDING_PROGRESS: OnboardingProgress = {
  hasCreatedEpic: false,
  hasCreatedRequest: false,
  hasRunOrchestration: false,
  setupCompleted: false,
  lastOnboardingStep: 0,
  dismissed: false,
  hasCompletedWizard: false,
  currentWizardStep: 0,
  firstLoginAt: null,
  completedAt: null,
  selectedPlatform: null,
  selectedDesignerPlatform: null,
  completedFlows: [],
  completedSteps: [],
  hasSeenHelpTooltip: false,
};

const VALID_PLATFORMS: readonly string[] = [
  "mac",
  "windows-powershell",
  "windows-cmd",
];
const VALID_DESIGNER_PLATFORMS: readonly string[] = ["web", "ios", "android"];

function loadOnboardingProgress(): OnboardingProgress {
  try {
    const raw = localStorage.getItem(ONBOARDING_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null) {
        const p = parsed as Record<string, unknown>;
        return {
          hasCreatedEpic:
            typeof p.hasCreatedEpic === "boolean" ? p.hasCreatedEpic : false,
          hasCreatedRequest:
            typeof p.hasCreatedRequest === "boolean"
              ? p.hasCreatedRequest
              : false,
          hasRunOrchestration:
            typeof p.hasRunOrchestration === "boolean"
              ? p.hasRunOrchestration
              : false,
          setupCompleted:
            typeof p.setupCompleted === "boolean" ? p.setupCompleted : false,
          lastOnboardingStep:
            typeof p.lastOnboardingStep === "number"
              ? p.lastOnboardingStep
              : 0,
          dismissed: typeof p.dismissed === "boolean" ? p.dismissed : false,
          hasCompletedWizard:
            typeof p.hasCompletedWizard === "boolean"
              ? p.hasCompletedWizard
              : false,
          currentWizardStep:
            typeof p.currentWizardStep === "number"
              ? p.currentWizardStep
              : 0,
          firstLoginAt:
            typeof p.firstLoginAt === "string" ? p.firstLoginAt : null,
          completedAt:
            typeof p.completedAt === "string" ? p.completedAt : null,
          selectedPlatform:
            typeof p.selectedPlatform === "string" &&
            VALID_PLATFORMS.includes(p.selectedPlatform)
              ? (p.selectedPlatform as Platform)
              : null,
          selectedDesignerPlatform:
            typeof p.selectedDesignerPlatform === "string" &&
            VALID_DESIGNER_PLATFORMS.includes(p.selectedDesignerPlatform)
              ? (p.selectedDesignerPlatform as DesignerPlatform)
              : null,
          completedFlows: Array.isArray(p.completedFlows)
            ? p.completedFlows.filter(
                (f): f is string => typeof f === "string",
              )
            : [],
          completedSteps: Array.isArray(p.completedSteps)
            ? p.completedSteps.filter(
                (stepId): stepId is string => typeof stepId === "string",
              )
            : [],
          hasSeenHelpTooltip:
            typeof p.hasSeenHelpTooltip === "boolean"
              ? p.hasSeenHelpTooltip
              : false,
        };
      }
    }
  } catch {
    // Ignore corrupt localStorage
  }
  return { ...DEFAULT_ONBOARDING_PROGRESS };
}

function saveOnboardingProgress(progress: OnboardingProgress): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(progress));
  } catch {
    // QuotaExceededError — silently fail
  }
}

// ---------------------------------------------------------------------------
// Store types
// ---------------------------------------------------------------------------

export type PanelState = "open" | "minimized";
export type HelpCenterDockMode = "modal" | "right";

export interface ChatState {
  // State
  messages: ChatMessage[];
  panelState: PanelState;
  panelSize: PanelSize;
  dockMode: DockMode;
  isQuietMode: boolean;
  isSoundEnabled: boolean;
  hasNotification: boolean;
  hasNewSuggestion: boolean;
  pendingSuggestions: SuggestedChip[];
  isThinking: boolean;
  isStreaming: boolean;
  /** Stores last user message text when an API call fails — enables retry */
  lastFailedMessage: string | null;
  /** Whether the full-screen mobile chat overlay is open */
  isMobileChatOpen: boolean;

  // Onboarding state (persisted to localStorage)
  onboardingProgress: OnboardingProgress;

  // Help Center transient UI state (NOT persisted)
  isHelpCenterOpen: boolean;
  helpCenterMode: "menu" | "wizard";
  helpCenterFlowId: string | null;
  helpCenterDockMode: HelpCenterDockMode;
  showHelpTooltip: boolean;

  // Actions
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void;
  updateMessageFeedback: (
    messageId: string,
    feedback: ChatMessage["feedback"]
  ) => void;
  /** Replace the last message in the list (used for error → retry swap) */
  replaceLastMessage: (message: ChatMessage) => void;
  setThinking: (thinking: boolean) => void;
  setStreaming: (streaming: boolean) => void;
  openPanel: () => void;
  closePanel: () => void;
  minimizePanel: () => void;
  togglePanel: () => void;
  setPanelSize: (size: PanelSize) => void;
  setQuietMode: (quiet: boolean) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setDockMode: (mode: DockMode) => void;
  setNotification: (has: boolean) => void;
  setNewSuggestion: (has: boolean) => void;
  setPendingSuggestions: (suggestions: SuggestedChip[]) => void;
  setLastFailedMessage: (text: string | null) => void;
  clearChatPreferences: () => void;
  /** Open the full-screen mobile chat overlay */
  openMobileChat: () => void;
  /** Close the full-screen mobile chat overlay */
  closeMobileChat: () => void;

  // Onboarding actions
  updateOnboardingProgress: (updates: Partial<OnboardingProgress>) => void;
  resetOnboardingProgress: () => void;
  markWizardComplete: () => void;
  setWizardStep: (step: number) => void;
  setSelectedPlatform: (platform: Platform) => void;
  setSelectedDesignerPlatform: (platform: DesignerPlatform | null) => void;
  markFlowComplete: (flowId: string) => void;
  markStepComplete: (stepId: string) => void;
  isStepComplete: (stepId: string) => boolean;

  // Help Center actions
  openHelpCenter: (mode?: "menu" | "wizard", flowId?: string) => void;
  closeHelpCenter: () => void;
  setHelpCenterDockMode: (mode: HelpCenterDockMode) => void;
  setShowHelpTooltip: (show: boolean) => void;
}

// ---------------------------------------------------------------------------
// Store implementation — purely ephemeral (no persist middleware)
// ---------------------------------------------------------------------------

export const useChatStore = create<ChatState>()((set, get) => {
  const savedPrefs = loadChatPreferences();

  return {
  // Initial state
  messages: [],
  panelState: "minimized",
  panelSize: savedPrefs.panelSize,
  dockMode: savedPrefs.dockMode,
  isQuietMode: savedPrefs.isQuietMode,
  isSoundEnabled: savedPrefs.isSoundEnabled,
  hasNotification: false,
  hasNewSuggestion: false,
  pendingSuggestions: [],
  isThinking: false,
  isStreaming: false,
  lastFailedMessage: null,
  isMobileChatOpen: false,

  // Onboarding state
  onboardingProgress: (() => {
    const saved = loadOnboardingProgress();
    // Set firstLoginAt on very first load
    if (!saved.firstLoginAt) {
      saved.firstLoginAt = new Date().toISOString();
      saveOnboardingProgress(saved);
    }
    return saved;
  })(),

  // Help Center state (transient)
  isHelpCenterOpen: false,
  helpCenterMode: "menu" as const,
  helpCenterFlowId: null as string | null,
  helpCenterDockMode: "modal" as HelpCenterDockMode,
  showHelpTooltip: false,

  // Message actions
  addMessage: (message) => {
    const state = get();
    const isNewAssistant = message.role === "assistant" && state.panelState === "minimized";
    set((s) => {
      const next = [...s.messages, message];
      return {
        messages: next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next,
        hasNewSuggestion: isNewAssistant ? true : s.hasNewSuggestion,
      };
    });
  },

  clearMessages: () => {
    set({
      messages: [],
      pendingSuggestions: [],
      hasNewSuggestion: false,
      lastFailedMessage: null,
      hasNotification: false,
      isThinking: false,
      isStreaming: false,
    });
  },

  // Feedback action
  updateMessageFeedback: (messageId, feedback) => {
    set((state) => ({
      messages: state.messages.map((msg) => {
        if (msg.id !== messageId) return msg;
        if (feedback === undefined) {
          // Remove feedback — create new object without the property
          const { feedback: _, ...rest } = msg;
          return rest as ChatMessage;
        }
        return { ...msg, feedback };
      }),
    }));
  },

  replaceLastMessage: (message) => {
    set((s) => {
      const msgs = [...s.messages];
      if (msgs.length > 0) msgs[msgs.length - 1] = message;
      return { messages: msgs };
    });
  },

  setLastFailedMessage: (text) => {
    set({ lastFailedMessage: text });
  },

  // AI state actions
  setThinking: (thinking) => {
    set({ isThinking: thinking });
  },

  setStreaming: (streaming) => {
    set({ isStreaming: streaming });
  },

  // Panel state actions
  openPanel: () => {
    set({ panelState: "open", hasNotification: false, hasNewSuggestion: false });
  },

  closePanel: () => {
    set({ panelState: "minimized", isMobileChatOpen: false });
  },

  minimizePanel: () => {
    set({ panelState: "minimized" });
  },

  togglePanel: () => {
    const current = get().panelState;
    if (current === "minimized") {
      set({ panelState: "open", hasNotification: false, hasNewSuggestion: false });
    } else {
      set({ panelState: "minimized" });
    }
  },

  setPanelSize: (size) => {
    set({ panelSize: size });
  },

  // Preference actions
  setQuietMode: (quiet) => {
    set({ isQuietMode: quiet });
  },

  setSoundEnabled: (enabled) => {
    set({ isSoundEnabled: enabled });
  },

  setDockMode: (mode) => {
    set({ dockMode: mode });
  },

  setNotification: (has) => {
    set({ hasNotification: has });
  },

  setNewSuggestion: (has) => {
    set({ hasNewSuggestion: has });
  },

  setPendingSuggestions: (suggestions) => {
    set({ pendingSuggestions: filterVisibleSuggestedChips(suggestions) });
  },

  clearChatPreferences: () => {
    // Reset to defaults in state. The subscription will auto-save,
    // which is the desired behavior — it writes defaults to sessionStorage.
    set({
      panelSize: DEFAULT_PANEL_SIZE,
      isSoundEnabled: DEFAULT_PREFS.isSoundEnabled,
      isQuietMode: DEFAULT_PREFS.isQuietMode,
      dockMode: DEFAULT_PREFS.dockMode,
    });
  },

  openMobileChat: () => {
    set({ isMobileChatOpen: true });
  },

  closeMobileChat: () => {
    set({ isMobileChatOpen: false, panelState: "minimized" });
  },

  // Onboarding actions
  updateOnboardingProgress: (updates) => {
    set((s) => {
      const next = { ...s.onboardingProgress, ...updates };
      saveOnboardingProgress(next);
      return { onboardingProgress: next };
    });
  },

  resetOnboardingProgress: () => {
    const fresh = {
      ...DEFAULT_ONBOARDING_PROGRESS,
      firstLoginAt: new Date().toISOString(),
    };
    saveOnboardingProgress(fresh);
    set({ onboardingProgress: fresh });
  },

  markWizardComplete: () => {
    set((s) => {
      const next = {
        ...s.onboardingProgress,
        hasCompletedWizard: true,
        completedAt: new Date().toISOString(),
      };
      saveOnboardingProgress(next);
      return { onboardingProgress: next };
    });
  },

  setWizardStep: (step) => {
    set((s) => {
      const next = { ...s.onboardingProgress, currentWizardStep: step };
      saveOnboardingProgress(next);
      return { onboardingProgress: next };
    });
  },

  setSelectedPlatform: (platform) => {
    set((s) => {
      const next = { ...s.onboardingProgress, selectedPlatform: platform };
      saveOnboardingProgress(next);
      return { onboardingProgress: next };
    });
  },

  setSelectedDesignerPlatform: (platform) => {
    set((s) => {
      const next = {
        ...s.onboardingProgress,
        selectedDesignerPlatform: platform,
      };
      saveOnboardingProgress(next);
      return { onboardingProgress: next };
    });
  },

  markFlowComplete: (flowId) => {
    const { onboardingProgress } = get();
    if (onboardingProgress.completedFlows.includes(flowId)) return;
    const next = {
      ...onboardingProgress,
      completedFlows: [...onboardingProgress.completedFlows, flowId],
    };
    saveOnboardingProgress(next);
    set({ onboardingProgress: next });
  },
  markStepComplete: (stepId) => {
    const { onboardingProgress } = get();
    if (onboardingProgress.completedSteps.includes(stepId)) return;
    const next = {
      ...onboardingProgress,
      completedSteps: [...onboardingProgress.completedSteps, stepId],
    };
    saveOnboardingProgress(next);
    set({ onboardingProgress: next });
  },
  isStepComplete: (stepId) => {
    return get().onboardingProgress.completedSteps.includes(stepId);
  },

  // Help Center actions
  openHelpCenter: (mode = "menu", flowId) => {
    set({
      isHelpCenterOpen: true,
      helpCenterMode: mode,
      helpCenterFlowId: flowId ?? null,
    });
  },

  closeHelpCenter: () => {
    set({ isHelpCenterOpen: false, helpCenterFlowId: null });
  },

  setHelpCenterDockMode: (mode) => {
    set({ helpCenterDockMode: mode });
  },

  setShowHelpTooltip: (show) => {
    set({ showHelpTooltip: show });
  },
};
});

// ---------------------------------------------------------------------------
// Auto-save preferences on change via subscription
// ---------------------------------------------------------------------------

let prevPrefs = {
  panelSize: useChatStore.getState().panelSize,
  isSoundEnabled: useChatStore.getState().isSoundEnabled,
  isQuietMode: useChatStore.getState().isQuietMode,
  dockMode: useChatStore.getState().dockMode,
};

useChatStore.subscribe((state) => {
  const currentPrefs = {
    panelSize: state.panelSize,
    isSoundEnabled: state.isSoundEnabled,
    isQuietMode: state.isQuietMode,
    dockMode: state.dockMode,
  };

  // Only save when preferences actually changed
  if (
    currentPrefs.panelSize !== prevPrefs.panelSize ||
    currentPrefs.isSoundEnabled !== prevPrefs.isSoundEnabled ||
    currentPrefs.isQuietMode !== prevPrefs.isQuietMode ||
    currentPrefs.dockMode !== prevPrefs.dockMode
  ) {
    prevPrefs = currentPrefs;
    saveChatPreferences(currentPrefs);
  }
});
